import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Divider,
  Chip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from 'react-toastify';
import logger from '../../utils/logger';
import useModalFocus from '../../hooks/useModalFocus';

const useStyles = makeStyles((theme) => ({
  dialog: {
    minWidth: 400,
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  statusText: {
    marginLeft: theme.spacing(1),
  },
}));

const LoggerConfig = ({ open, onClose }) => {
  const classes = useStyles();
  const [config, setConfig] = useState({});
  const [isEnabled, setIsEnabled] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, title: '', message: '' });
  
  // Usar el hook personalizado para manejar el foco
  const { modalRef, handleClose: handleModalClose, handleExited } = useModalFocus(open);

  useEffect(() => {
    if (open) {
      const currentConfig = logger.config.get();
      setConfig(currentConfig);
      setIsEnabled(currentConfig.enableConsole);
      logger.debug("🔧 Modal de configuración de logs abierto");
      
      // Logs de prueba para verificar funcionamiento
      logger.sw.debug("🧪 LOG DE PRUEBA - Service Worker");
      logger.socket.debug("🧪 LOG DE PRUEBA - Socket");
      logger.whatsapp.debug("🧪 LOG DE PRUEBA - WhatsApp");
      logger.transferModal.debug("🧪 LOG DE PRUEBA - Transfer Modal");
      logger.dashboard.debug("🧪 LOG DE PRUEBA - Dashboard");
    }
  }, [open]);

  const handleToggleCategory = (category) => {
    const newConfig = {
      ...config,
      [`enable${category}`]: !config[`enable${category}`]
    };
    setConfig(newConfig);
    logger.config.set(newConfig);
    logger.debug(`🔧 Categoría ${category} ${newConfig[`enable${category}`] ? 'activada' : 'desactivada'}`);
  };

  const handleToggleConsole = () => {
    const newConfig = {
      ...config,
      enableConsole: !isEnabled
    };
    setConfig(newConfig);
    setIsEnabled(!isEnabled);
    logger.config.set(newConfig);
  };

  // ✅ FUNCIONES DE CONFIRMACIÓN
  const showConfirmDialog = (action, title, message) => {
    setConfirmDialog({
      open: true,
      action,
      title,
      message
    });
  };

  const handleConfirmAction = async () => {
    const { action } = confirmDialog;
    setConfirmDialog({ open: false, action: null, title: '', message: '' });
    
    if (action === 'clear') {
      await executeClearStorage();
    } else if (action === 'reset') {
      await executeReset();
    }
  };

  const handleCancelAction = () => {
    setConfirmDialog({ open: false, action: null, title: '', message: '' });
  };

  // ✅ FUNCIONES DE ACCIÓN CON CONFIRMACIÓN
  const handleReset = () => {
    showConfirmDialog(
      'reset',
      '🔄 Restablecer Configuración',
      '¿Estás seguro de que quieres restablecer la configuración de logs a los valores por defecto? Esta acción no se puede deshacer.'
    );
  };

  const handleClearStorage = () => {
    showConfirmDialog(
      'clear',
      '🗑️ Limpiar Storage',
      '¿Estás seguro de que quieres limpiar el storage de configuración de logs? Esta acción eliminará todas las configuraciones personalizadas y no se puede deshacer.'
    );
  };

  // ✅ FUNCIONES DE EJECUCIÓN REAL
  const executeReset = async () => {
    setIsResetting(true);
    
    try {
      // Simular un pequeño delay para mostrar el feedback visual
      await new Promise(resolve => setTimeout(resolve, 500));
      
      logger.config.reset();
      const defaultConfig = logger.config.get();
      setConfig(defaultConfig);
      setIsEnabled(defaultConfig.enableConsole);
      
      // ✅ FEEDBACK VISUAL: Toast de confirmación
      toast.success("✅ Configuración restablecida a valores por defecto", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      logger.debug("🔄 Configuración reseteada a valores por defecto");
      
    } catch (error) {
      // ✅ FEEDBACK VISUAL: Toast de error
      toast.error("❌ Error al restablecer configuración", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      logger.error("Error al restablecer configuración:", error);
    } finally {
      setIsResetting(false);
    }
  };

  const executeClearStorage = async () => {
    setIsClearing(true);
    
    try {
      // Simular un pequeño delay para mostrar el feedback visual
      await new Promise(resolve => setTimeout(resolve, 500));
      
      localStorage.removeItem('loggerConfig');
      const defaultConfig = logger.config.get();
      setConfig(defaultConfig);
      setIsEnabled(defaultConfig.enableConsole);
      
      // ✅ FEEDBACK VISUAL: Toast de confirmación
      toast.success("✅ Storage limpiado exitosamente", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      logger.debug("🗑️ localStorage limpiado y configuración reseteada");
      
    } catch (error) {
      // ✅ FEEDBACK VISUAL: Toast de error
      toast.error("❌ Error al limpiar storage", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      logger.error("Error al limpiar storage:", error);
    } finally {
      setIsClearing(false);
    }
  };

  const handleClose = (event) => {
    // Usar el hook para manejar el foco correctamente
    handleModalClose(event);
    
    // Llamar al callback original
    onClose();
  };

  const getStatusColor = () => {
    if (!isEnabled) return 'default';
    const enabledCategories = Object.keys(config).filter(key => 
      key.startsWith('enable') && key !== 'enableConsole' && config[key]
    ).length;
    if (enabledCategories === 0) return 'secondary';
    return 'primary';
  };

  const getStatusText = () => {
    if (!isEnabled) return 'Logs deshabilitados';
    const enabledCategories = Object.keys(config).filter(key => 
      key.startsWith('enable') && key !== 'enableConsole' && config[key]
    ).length;
    if (enabledCategories === 0) return 'Logs habilitados pero sin categorías activas';
    return `${enabledCategories} categoría(s) activa(s)`;
  };

  return (
    <>
      <Dialog 
        ref={modalRef}
        open={open} 
        onClose={handleClose} 
        className={classes.dialog}
        disableEnforceFocus={false}
        disableAutoFocus={false}
      disableRestoreFocus={false}
      keepMounted={false}
      disablePortal={false}
      hideBackdrop={false}
      disableScrollLock={false}
      disableEscapeKeyDown={false}
      fullWidth={false}
      maxWidth="sm"
      TransitionProps={{
        onExited: handleExited
      }}
      PaperProps={{
        'aria-modal': 'true',
        role: 'dialog',
        'aria-labelledby': 'logger-config-title'
      }}
    >
      <DialogTitle id="logger-config-title">
        Configuración de Logs
      </DialogTitle>
      <DialogContent>
        <Box className={classes.status}>
          <Chip 
            label={getStatusText()} 
            color={getStatusColor()}
            size="small"
          />
        </Box>

        <Divider style={{ marginBottom: 16 }} />

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Control General
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={isEnabled}
                onChange={handleToggleConsole}
                color="primary"
              />
            }
            label="Habilitar logs en consola"
          />
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Categorías de Logs
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={config.enableServiceWorker || false}
                onChange={() => handleToggleCategory('ServiceWorker')}
                disabled={!isEnabled}
                color="primary"
              />
            }
            label="Service Worker"
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.enableSocket || false}
                onChange={() => handleToggleCategory('Socket')}
                disabled={!isEnabled}
                color="primary"
              />
            }
            label="Socket/WebSocket"
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.enableWhatsApp || false}
                onChange={() => handleToggleCategory('WhatsApp')}
                disabled={!isEnabled}
                color="primary"
              />
            }
            label="WhatsApp"
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.enableTransferModal || false}
                onChange={() => handleToggleCategory('TransferModal')}
                disabled={!isEnabled}
                color="primary"
              />
            }
            label="Modal de Transferencia"
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.enableDashboard || false}
                onChange={() => handleToggleCategory('Dashboard')}
                disabled={!isEnabled}
                color="primary"
              />
            }
            label="Dashboard"
          />
        </Box>

        <Box className={classes.section}>
          <Typography variant="body2" color="textSecondary">
            Los cambios se aplican inmediatamente. Los logs se guardan en localStorage.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleClearStorage} 
          color="secondary" 
          style={{ marginRight: 'auto' }}
          disabled={isClearing || isResetting}
        >
          {isClearing ? "Limpiando..." : "Limpiar Storage"}
        </Button>
        <Button 
          onClick={handleReset} 
          color="secondary"
          disabled={isClearing || isResetting}
        >
          {isResetting ? "Restableciendo..." : "Restablecer"}
        </Button>
        <Button 
          onClick={handleClose} 
          color="primary"
          autoFocus={false}
          tabIndex={-1}
          disabled={isClearing || isResetting}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>

    {/* ✅ DIÁLOGO DE CONFIRMACIÓN */}
    <Dialog
      open={confirmDialog.open}
      onClose={handleCancelAction}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
    >
      <DialogTitle id="confirm-dialog-title">
        {confirmDialog.title}
      </DialogTitle>
      <DialogContent>
        <Typography id="confirm-dialog-description">
          {confirmDialog.message}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelAction} color="primary">
          Cancelar
        </Button>
        <Button 
          onClick={handleConfirmAction} 
          color="secondary" 
          variant="contained"
          autoFocus
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>

    {/* ✅ DIÁLOGO DE CONFIRMACIÓN */}
    <Dialog
      open={confirmDialog.open}
      onClose={handleCancelAction}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
    >
      <DialogTitle id="confirm-dialog-title">
        {confirmDialog.title}
      </DialogTitle>
      <DialogContent>
        <Typography id="confirm-dialog-description">
          {confirmDialog.message}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelAction} color="primary">
          Cancelar
        </Button>
        <Button 
          onClick={handleConfirmAction} 
          color="secondary" 
          variant="contained"
          autoFocus
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default LoggerConfig; 